/**
 * Zakładka „Migracja z WF" na szczegółach tenanta — panel ITE (ADR-0111, faza 27).
 *
 * Wdrożeniowiec zakłada instalację, dostaje token do wklejenia w jej konfigurację, podpina
 * stare bazy klienta, widzi, czy program żyje, zleca rozpoznanie schematu, ustawia mapowanie
 * i uruchamia przeniesienie. Rozstrzyganie konfliktów trafi do panelu KLIENTA, nie tutaj —
 * to pytanie o jego dane, na które wdrożeniowiec nie zna odpowiedzi.
 *
 * Dwie rzeczy, które ekran musi komunikować mocniej niż zwykły formularz:
 *  - TOKEN POKAZUJEMY RAZ. Nie da się go odzyskać, bo w bazie leży tylko jego skrót.
 *    Stąd osobne okno „skopiuj teraz" i ostrzeżenie przy generowaniu nowego — nowy token
 *    natychmiast unieważnia poprzedni i program u klienta zamilknie, dopóki ktoś nie wklei
 *    nowego.
 *  - WŁĄCZENIE ZAPISU oznacza, że zaczniemy pisać do PRODUKCYJNEJ bazy klienta. Dlatego
 *    jest za potwierdzeniem w czerwonym wariancie, a nie za zwykłym przełącznikiem.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Database,
  GitCompare,
  Play,
  PlugZap,
  Plus,
  RefreshCw,
  ScanSearch,
  SlidersHorizontal,
  Upload,
} from 'lucide-react';

import { Alert } from '@/components/Alert';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { Skeleton } from '@/components/Skeleton';
import { Table, type TableColumn } from '@/components/Table';
import { LegacyConflictsModal } from '@/components/tenant-detail/LegacyConflictsModal';
import { LegacyMappingModal } from '@/components/tenant-detail/LegacyMappingModal';
import { LegacyMigrationRunModal } from '@/components/tenant-detail/LegacyMigrationRunModal';
import { LegacyPushModal } from '@/components/tenant-detail/LegacyPushModal';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { extractErrorCode } from '@/lib/errors';

interface SourceRow {
  id: string;
  sourceKey: string;
  name: string;
  writeEnabled: boolean;
  schemaRecognizedAt: string | null;
  lastSyncAt: string | null;
}

interface AgentRow {
  agentId: string;
  agentVersion: string | null;
  lastSeenAt: string;
  online: boolean;
}

interface Installation {
  id: string;
  name: string;
  status: string;
  hasToken: boolean;
  tokenGeneratedAt: string | null;
  lastSyncAt: string | null;
  notes: string | null;
  sources: SourceRow[];
  agents: AgentRow[];
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pl-PL');
  } catch {
    return iso;
  }
}

export interface TenantLegacyMigrationTabProps {
  tenantId: string;
}

export function TenantLegacyMigrationTab({ tenantId }: TenantLegacyMigrationTabProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [sourceFor, setSourceFor] = useState<string | null>(null);
  const [sourceKey, setSourceKey] = useState('');
  const [sourceName, setSourceName] = useState('');
  const [issuedToken, setIssuedToken] = useState<string | null>(null);
  const [regenerateFor, setRegenerateFor] = useState<string | null>(null);
  const [writeToggle, setWriteToggle] = useState<{
    migrationId: string;
    source: SourceRow;
  } | null>(null);
  const [detach, setDetach] = useState<{ migrationId: string; source: SourceRow } | null>(null);
  const [rozbieznosci, setRozbieznosci] = useState<{
    migrationId: string;
    source: SourceRow;
  } | null>(null);
  const [mapowanie, setMapowanie] = useState<{ migrationId: string; source: SourceRow } | null>(
    null,
  );
  const [przebieg, setPrzebieg] = useState<{ migrationId: string; source: SourceRow } | null>(null);
  const [zapis, setZapis] = useState<{ migrationId: string; source: SourceRow } | null>(null);

  const queryKey = ['legacy-migration', tenantId];
  const basePath = `/admin/tenants/${tenantId}/legacy-migration`;

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await api.get<{ installations: Installation[] }>(basePath);
      return res.data.installations;
    },
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey });

  const fail = (err: unknown, fallback: string) => {
    const code = extractErrorCode(err);
    toast.show({
      variant: 'error',
      title: code ? t(`admin.tenants.legacyMigration.errors.${code}`, fallback) : fallback,
    });
  };

  const createInstallation = useMutation({
    mutationFn: async () => {
      const res = await api.post<{ id: string; plainToken: string }>(basePath, { name: newName });
      return res.data;
    },
    onSuccess: (res) => {
      setAddOpen(false);
      setNewName('');
      setIssuedToken(res.plainToken);
      refresh();
    },
    onError: (err) => fail(err, t('admin.tenants.legacyMigration.toast.createFailed')),
  });

  const setStatus = useMutation({
    mutationFn: async (vars: { id: string; status: 'active' | 'paused' }) => {
      await api.patch(`${basePath}/${vars.id}`, { status: vars.status });
    },
    onSuccess: () => {
      toast.show({
        variant: 'success',
        title: t('admin.tenants.legacyMigration.toast.statusChanged'),
      });
      refresh();
    },
    onError: (err) => fail(err, t('admin.tenants.legacyMigration.toast.statusFailed')),
  });

  const regenerate = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<{ plainToken: string }>(`${basePath}/${id}/regenerate-token`);
      return res.data;
    },
    onSuccess: (res) => {
      setRegenerateFor(null);
      setIssuedToken(res.plainToken);
      refresh();
    },
    onError: (err) => fail(err, t('admin.tenants.legacyMigration.toast.tokenFailed')),
  });

  const addSource = useMutation({
    mutationFn: async (migrationId: string) => {
      await api.post(`${basePath}/${migrationId}/sources`, {
        sourceKey: sourceKey.trim(),
        name: sourceName.trim(),
      });
    },
    onSuccess: () => {
      setSourceFor(null);
      setSourceKey('');
      setSourceName('');
      toast.show({
        variant: 'success',
        title: t('admin.tenants.legacyMigration.toast.sourceAdded'),
      });
      refresh();
    },
    onError: (err) => fail(err, t('admin.tenants.legacyMigration.toast.sourceFailed')),
  });

  const changeSource = useMutation({
    mutationFn: async (vars: { migrationId: string; sourceId: string; writeEnabled: boolean }) => {
      await api.patch(`${basePath}/${vars.migrationId}/sources/${vars.sourceId}`, {
        writeEnabled: vars.writeEnabled,
      });
    },
    onSuccess: () => {
      setWriteToggle(null);
      toast.show({
        variant: 'success',
        title: t('admin.tenants.legacyMigration.toast.sourceChanged'),
      });
      refresh();
    },
    onError: (err) => fail(err, t('admin.tenants.legacyMigration.toast.sourceFailed')),
  });

  const zlecZadanie = useMutation({
    mutationFn: async (vars: {
      migrationId: string;
      sourceId: string;
      jobType: 'test_connection' | 'discover_schema';
    }) => {
      await api.post(`${basePath}/${vars.migrationId}/sources/${vars.sourceId}/jobs`, {
        jobType: vars.jobType,
      });
    },
    onSuccess: () => {
      toast.show({
        variant: 'success',
        title: t('admin.tenants.legacyMigration.toast.jobQueued'),
      });
      refresh();
    },
    onError: (err) => fail(err, t('admin.tenants.legacyMigration.toast.jobFailed')),
  });

  const detachSource = useMutation({
    mutationFn: async (vars: { migrationId: string; sourceId: string }) => {
      await api.delete(`${basePath}/${vars.migrationId}/sources/${vars.sourceId}`);
    },
    onSuccess: () => {
      setDetach(null);
      toast.show({
        variant: 'success',
        title: t('admin.tenants.legacyMigration.toast.sourceDetached'),
      });
      refresh();
    },
    onError: (err) => fail(err, t('admin.tenants.legacyMigration.toast.sourceFailed')),
  });

  const sourceColumns = (migrationId: string): TableColumn<SourceRow>[] => [
    {
      key: 'sourceKey',
      header: t('admin.tenants.legacyMigration.sources.key'),
      accessor: (row) => <code className="text-sm">{row.sourceKey}</code>,
    },
    {
      key: 'name',
      header: t('admin.tenants.legacyMigration.sources.name'),
      accessor: (row) => row.name,
    },
    {
      key: 'write',
      header: t('admin.tenants.legacyMigration.sources.write'),
      accessor: (row) => (
        <Badge color={row.writeEnabled ? 'amber' : 'gray'}>
          {row.writeEnabled
            ? t('admin.tenants.legacyMigration.sources.writeOn')
            : t('admin.tenants.legacyMigration.sources.writeOff')}
        </Badge>
      ),
    },
    {
      key: 'recognized',
      header: t('admin.tenants.legacyMigration.sources.recognized'),
      accessor: (row) => formatDateTime(row.schemaRecognizedAt),
    },
    {
      key: 'actions',
      header: '',
      accessor: (row) => (
        <div className="flex flex-wrap justify-end gap-2">
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<PlugZap className="size-4" />}
            disabled={zlecZadanie.isPending}
            onClick={() =>
              zlecZadanie.mutate({ migrationId, sourceId: row.id, jobType: 'test_connection' })
            }
          >
            {t('admin.tenants.legacyMigration.sources.testConnection')}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            leftIcon={<ScanSearch className="size-4" />}
            disabled={zlecZadanie.isPending}
            onClick={() =>
              zlecZadanie.mutate({ migrationId, sourceId: row.id, jobType: 'discover_schema' })
            }
          >
            {t('admin.tenants.legacyMigration.sources.discover')}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            leftIcon={<SlidersHorizontal className="size-4" />}
            onClick={() => setMapowanie({ migrationId, source: row })}
          >
            {t('admin.tenants.legacyMigration.sources.mapping')}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            leftIcon={<Play className="size-4" />}
            onClick={() => setPrzebieg({ migrationId, source: row })}
          >
            {t('admin.tenants.legacyMigration.sources.run')}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            leftIcon={<GitCompare className="size-4" />}
            onClick={() => setRozbieznosci({ migrationId, source: row })}
          >
            {t('admin.tenants.legacyMigration.sources.conflicts')}
          </Button>
          {/*
            Zapis do starej bazy stoi PO „Rozbieżnościach" celowo: rozbieżności trzeba
            rozstrzygnąć zanim cokolwiek wypchniemy, bo rekord z nierozstrzygniętą różnicą
            i tak nie pojedzie — a kolejność przycisków jest pierwszą podpowiedzią kolejności
            pracy, jaką wdrożeniowiec dostaje.
          */}
          <Button
            size="sm"
            variant="secondary"
            leftIcon={<Upload className="size-4" />}
            onClick={() => setZapis({ migrationId, source: row })}
          >
            {t('admin.tenants.legacyMigration.sources.push')}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setWriteToggle({ migrationId, source: row })}
          >
            {row.writeEnabled
              ? t('admin.tenants.legacyMigration.sources.disableWrite')
              : t('admin.tenants.legacyMigration.sources.enableWrite')}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setDetach({ migrationId, source: row })}>
            {t('admin.tenants.legacyMigration.sources.detach')}
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading) return <Skeleton className="h-48 w-full" />;

  if (error) {
    return (
      <Alert tone="error" title={t('admin.tenants.legacyMigration.loadFailed')}>
        {extractErrorCode(error) === 'TENANT_DB_UNAVAILABLE'
          ? t('admin.tenants.legacyMigration.errors.TENANT_DB_UNAVAILABLE')
          : t('common.retry')}
      </Alert>
    );
  }

  const installations = data ?? [];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-[var(--text-secondary)]">
          {t('admin.tenants.legacyMigration.intro')}
        </p>
        <Button onClick={() => setAddOpen(true)} leftIcon={<Plus className="size-4" />}>
          {t('admin.tenants.legacyMigration.add')}
        </Button>
      </div>

      {installations.length === 0 ? (
        <Card>
          <div className="flex flex-col items-center gap-2 py-8 text-center">
            <Database className="size-8 text-[var(--text-tertiary)]" />
            <p className="text-sm text-[var(--text-secondary)]">
              {t('admin.tenants.legacyMigration.empty')}
            </p>
          </div>
        </Card>
      ) : (
        installations.map((inst) => (
          <Card key={inst.id}>
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{inst.name}</span>
                    <Badge color={inst.status === 'active' ? 'green' : 'gray'}>
                      {inst.status === 'active'
                        ? t('admin.tenants.legacyMigration.status.active')
                        : t('admin.tenants.legacyMigration.status.paused')}
                    </Badge>
                  </div>
                  <span className="text-sm text-[var(--text-secondary)]">
                    {t('admin.tenants.legacyMigration.tokenGenerated', {
                      date: formatDateTime(inst.tokenGeneratedAt),
                    })}
                  </span>
                </div>

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      setStatus.mutate({
                        id: inst.id,
                        status: inst.status === 'active' ? 'paused' : 'active',
                      })
                    }
                  >
                    {inst.status === 'active'
                      ? t('admin.tenants.legacyMigration.pause')
                      : t('admin.tenants.legacyMigration.resume')}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    leftIcon={<RefreshCw className="size-4" />}
                    onClick={() => setRegenerateFor(inst.id)}
                  >
                    {t('admin.tenants.legacyMigration.regenerate')}
                  </Button>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium">
                  {t('admin.tenants.legacyMigration.agents.title')}
                </span>
                {inst.agents.length === 0 ? (
                  <span className="text-sm text-[var(--text-secondary)]">
                    {t('admin.tenants.legacyMigration.agents.none')}
                  </span>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {inst.agents.map((agent) => (
                      <Badge key={agent.agentId} color={agent.online ? 'green' : 'gray'}>
                        {agent.agentId}
                        {agent.agentVersion ? ` · ${agent.agentVersion}` : ''} ·{' '}
                        {formatDateTime(agent.lastSeenAt)}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">
                    {t('admin.tenants.legacyMigration.sources.title')}
                  </span>
                  <Button
                    size="sm"
                    variant="secondary"
                    leftIcon={<Plus className="size-4" />}
                    onClick={() => setSourceFor(inst.id)}
                  >
                    {t('admin.tenants.legacyMigration.sources.add')}
                  </Button>
                </div>
                <Table
                  columns={sourceColumns(inst.id)}
                  rows={inst.sources}
                  rowKey={(row) => row.id}
                  emptyState={t('admin.tenants.legacyMigration.sources.empty')}
                />
              </div>
            </div>
          </Card>
        ))
      )}

      <Modal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title={t('admin.tenants.legacyMigration.addModal.title')}
      >
        <div className="flex flex-col gap-4">
          <FormField label={t('admin.tenants.legacyMigration.addModal.name')}>
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} autoFocus />
          </FormField>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setAddOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => createInstallation.mutate()}
              disabled={newName.trim().length === 0 || createInstallation.isPending}
            >
              {t('common.save')}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={sourceFor !== null}
        onClose={() => setSourceFor(null)}
        title={t('admin.tenants.legacyMigration.sourceModal.title')}
      >
        <div className="flex flex-col gap-4">
          <Alert tone="info">{t('admin.tenants.legacyMigration.sourceModal.keyWarning')}</Alert>
          <FormField
            label={t('admin.tenants.legacyMigration.sourceModal.key')}
            hint={t('admin.tenants.legacyMigration.sourceModal.keyHint')}
          >
            <Input value={sourceKey} onChange={(e) => setSourceKey(e.target.value)} autoFocus />
          </FormField>
          <FormField label={t('admin.tenants.legacyMigration.sourceModal.name')}>
            <Input value={sourceName} onChange={(e) => setSourceName(e.target.value)} />
          </FormField>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setSourceFor(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => sourceFor && addSource.mutate(sourceFor)}
              disabled={
                sourceKey.trim().length === 0 ||
                sourceName.trim().length === 0 ||
                addSource.isPending
              }
            >
              {t('common.save')}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={issuedToken !== null}
        onClose={() => setIssuedToken(null)}
        title={t('admin.tenants.legacyMigration.tokenModal.title')}
      >
        <div className="flex flex-col gap-4">
          <Alert tone="warning" title={t('admin.tenants.legacyMigration.tokenModal.onceTitle')}>
            {t('admin.tenants.legacyMigration.tokenModal.once')}
          </Alert>
          <code className="block break-all rounded-md bg-[var(--surface-secondary)] p-3 text-sm">
            {issuedToken}
          </code>
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                if (issuedToken) void navigator.clipboard?.writeText(issuedToken);
                toast.show({
                  variant: 'success',
                  title: t('admin.tenants.legacyMigration.tokenModal.copied'),
                });
              }}
            >
              {t('admin.tenants.legacyMigration.tokenModal.copy')}
            </Button>
            <Button onClick={() => setIssuedToken(null)}>{t('common.close')}</Button>
          </div>
        </div>
      </Modal>

      <LegacyMappingModal
        open={mapowanie !== null}
        onClose={() => setMapowanie(null)}
        tenantId={tenantId}
        migrationId={mapowanie?.migrationId ?? ''}
        source={mapowanie?.source ? { id: mapowanie.source.id, name: mapowanie.source.name } : null}
      />

      <LegacyConflictsModal
        open={rozbieznosci !== null}
        onClose={() => setRozbieznosci(null)}
        tenantId={tenantId}
        migrationId={rozbieznosci?.migrationId ?? ''}
        source={
          rozbieznosci?.source
            ? { id: rozbieznosci.source.id, name: rozbieznosci.source.name }
            : null
        }
      />

      <LegacyPushModal
        open={zapis !== null}
        onClose={() => setZapis(null)}
        tenantId={tenantId}
        migrationId={zapis?.migrationId ?? ''}
        source={
          zapis?.source
            ? {
                id: zapis.source.id,
                name: zapis.source.name,
                writeEnabled: zapis.source.writeEnabled,
              }
            : null
        }
      />

      <LegacyMigrationRunModal
        open={przebieg !== null}
        onClose={() => setPrzebieg(null)}
        tenantId={tenantId}
        migrationId={przebieg?.migrationId ?? ''}
        source={przebieg?.source ? { id: przebieg.source.id, name: przebieg.source.name } : null}
      />

      <ConfirmDialog
        open={regenerateFor !== null}
        title={t('admin.tenants.legacyMigration.regenerateConfirm.title')}
        message={t('admin.tenants.legacyMigration.regenerateConfirm.message')}
        confirmLabel={t('admin.tenants.legacyMigration.regenerateConfirm.confirm')}
        variant="danger"
        onConfirm={() => regenerateFor && regenerate.mutate(regenerateFor)}
        onClose={() => setRegenerateFor(null)}
      />

      <ConfirmDialog
        open={writeToggle !== null}
        title={
          writeToggle?.source.writeEnabled
            ? t('admin.tenants.legacyMigration.writeConfirm.disableTitle')
            : t('admin.tenants.legacyMigration.writeConfirm.enableTitle')
        }
        message={
          writeToggle?.source.writeEnabled
            ? t('admin.tenants.legacyMigration.writeConfirm.disableMessage')
            : t('admin.tenants.legacyMigration.writeConfirm.enableMessage', {
                name: writeToggle?.source.name ?? '',
              })
        }
        variant={writeToggle?.source.writeEnabled ? 'primary' : 'danger'}
        confirmLabel={
          writeToggle?.source.writeEnabled
            ? t('admin.tenants.legacyMigration.writeConfirm.disableConfirm')
            : t('admin.tenants.legacyMigration.writeConfirm.enableConfirm')
        }
        onConfirm={() =>
          writeToggle &&
          changeSource.mutate({
            migrationId: writeToggle.migrationId,
            sourceId: writeToggle.source.id,
            writeEnabled: !writeToggle.source.writeEnabled,
          })
        }
        onClose={() => setWriteToggle(null)}
      />

      <ConfirmDialog
        open={detach !== null}
        title={t('admin.tenants.legacyMigration.detachConfirm.title')}
        message={t('admin.tenants.legacyMigration.detachConfirm.message', {
          name: detach?.source.name ?? '',
        })}
        variant="danger"
        onConfirm={() =>
          detach &&
          detachSource.mutate({ migrationId: detach.migrationId, sourceId: detach.source.id })
        }
        onClose={() => setDetach(null)}
      />
    </div>
  );
}
